import { useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getMe } from '@/api/services/auth.service'
import { useAuthStore } from '@/stores/useAuthStore'
import { exchangeSupportCode } from './api'

/**
 * Where the operator console's link lands. Exchanges the one-time code for a
 * read-only support session, then opens the dashboard as that user.
 */
export function SupportSessionPage(): React.ReactElement {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setAuth = useAuthStore((s) => s.setAuth)
  const code = params.get('code')
  const [failure, setFailure] = useState<string | null>(null)
  const error = code ? failure : 'This link is missing its code.'
  // The code works once: StrictMode's double effect must not spend it twice.
  const started = useRef(false)

  useEffect(() => {
    if (started.current || !code) return
    started.current = true
    exchangeSupportCode(code)
      .then(() => getMe())
      .then((user) => {
        // Nothing cached from whoever used this browser before may show
        // through into the customer's view.
        queryClient.clear()
        setAuth(user)
        navigate('/dashboard', { replace: true })
      })
      .catch((caught: unknown) => {
        const message =
          isAxiosError(caught) && typeof caught.response?.data?.message === 'string'
            ? caught.response.data.message
            : 'The support session could not be opened.'
        setFailure(message)
      })
  }, [code, navigate, queryClient, setAuth])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
      <h1 className="text-lg font-medium text-gray-900 dark:text-gray-100">
        {error ? 'Support session not opened' : 'Opening a read-only support session…'}
      </h1>
      {error && <p className="max-w-md text-sm text-gray-600 dark:text-gray-400">{error}</p>}
    </div>
  )
}
